import { createContext, useCallback, useContext, useMemo } from 'react';
import type { FormNameKey } from '../@types/common-data-types';
import { useAppSelector } from '../redux/hooks';
import { selectEscalationFactors } from '../redux/slices/diagram';
import { useConfigurationContext } from './configuration.context';
import { useDiagramContext } from './diagram.context';
import { useUserPermissionsContext } from './user-permissions.context';

const createUniformPermissionRecord = (value: boolean): Record<FormNameKey, boolean> => ({
  main: value,
  causes: value,
  consequences: value,
  controls: value,
  escalationFactors: value,
  escalationFactorBarriers: value,
});

const allAppPermissions = createUniformPermissionRecord(true);

type AppPermissions = {
  canCreateBackendRecordsPermission: boolean;
  addNodePermission: Record<FormNameKey, boolean>;
  editNodePermission: Record<FormNameKey, boolean>;
  removeNodePermission: Record<FormNameKey, boolean>;

  resolvePermissionsByNodeType: (type?: string) => {
    addNodePermission: boolean;
    editNodePermission: boolean;
    removeNodePermission: boolean;
  };
};

type AppPermissionsContextProps = React.PropsWithChildren;

const AppPermissionsContext = createContext<AppPermissions | undefined>(undefined);

export const AppPermissionsProvider = ({ children }: AppPermissionsContextProps) => {
  const { isAIGenerated, isNewDiagram, isSavedDiagram } = useDiagramContext();
  const {
    createRecord: apiPermissionCreateRecord,
    editRecord: apiPermissionEditRecord,
    deleteRecord: apiPermissionsDeleteRecord,
    isSuperUser,
  } = useUserPermissionsContext();
  const escalationFactors = useAppSelector(selectEscalationFactors);
  const { escalationFactors: efConfig, escalationFactorBarriers: efbConfig } = useConfigurationContext();
  const tenantHasEfConfig = Boolean(efConfig);
  const tenantHasEfbConfig = Boolean(efbConfig);

  const { addNodePermission, editNodePermission, removeNodePermission, canCreateBackendRecordsPermission } =
    useMemo(() => {
      const isRecordBacked = !isAIGenerated && !isNewDiagram && !isSavedDiagram;

      // For non-record-backed diagrams (new/AI/saved), selectEscalationFactors is authoritative,
      // so EF/EFB create perms are only required when the diagram actually contains those node types.
      // For record-backed diagrams the redux state cannot be trusted — useRecord (use-record.ts:411-418)
      // silently drops EFs when EFB config is missing — so we gate on tenant config presence instead:
      // if the tenant has no EF / EFB form configured, the source record cannot contain those nodes
      // and the corresponding perm requirement can be skipped.
      const diagramHasEscalationFactors = (escalationFactors?.length ?? 0) > 0;
      const diagramHasEscalationFactorBarriers =
        escalationFactors?.some((ef) => (ef.barriers?.length ?? 0) > 0) ?? false;

      const efRequirementMet = isRecordBacked
        ? !tenantHasEfConfig || apiPermissionCreateRecord.escalationFactors
        : !diagramHasEscalationFactors || apiPermissionCreateRecord.escalationFactors;
      const efbRequirementMet = isRecordBacked
        ? !tenantHasEfbConfig || apiPermissionCreateRecord.escalationFactorBarriers
        : !diagramHasEscalationFactorBarriers || apiPermissionCreateRecord.escalationFactorBarriers;

      const canCreateBackendRecordsPermission =
        isSuperUser === false &&
        apiPermissionCreateRecord.main &&
        apiPermissionCreateRecord.causes &&
        apiPermissionCreateRecord.consequences &&
        apiPermissionCreateRecord.controls &&
        efRequirementMet &&
        efbRequirementMet;

      if (isRecordBacked) {
        // we have a record id, therefore the user permissions should be used
        return {
          addNodePermission: apiPermissionCreateRecord,
          editNodePermission: apiPermissionEditRecord,
          removeNodePermission: apiPermissionsDeleteRecord,
          canCreateBackendRecordsPermission,
        };
      } else {
        // we don't have a record id, therefore the user can add, edit and remove nodes
        return {
          addNodePermission: allAppPermissions,
          editNodePermission: allAppPermissions,
          removeNodePermission: allAppPermissions,
          canCreateBackendRecordsPermission,
        };
      }
    }, [
      isAIGenerated,
      isNewDiagram,
      isSavedDiagram,
      isSuperUser,
      apiPermissionCreateRecord,
      apiPermissionEditRecord,
      apiPermissionsDeleteRecord,
      escalationFactors,
      tenantHasEfConfig,
      tenantHasEfbConfig,
    ]);

  const resolvePermissionsByNodeType = useCallback(
    (type?: string) => {
      if ('mue-node' === type || 'hazard-node' === type) {
        return {
          addNodePermission: addNodePermission.main,
          editNodePermission: editNodePermission.main,
          removeNodePermission: removeNodePermission.main,
        };
      } else if ('cause-node' === type) {
        return {
          addNodePermission: addNodePermission.causes,
          editNodePermission: editNodePermission.causes,
          removeNodePermission: removeNodePermission.causes,
        };
      } else if ('consequence-node' === type) {
        return {
          addNodePermission: addNodePermission.consequences,
          editNodePermission: editNodePermission.consequences,
          removeNodePermission: removeNodePermission.consequences,
        };
      } else if ('preventative-control-node' === type || 'mitigating-control-node' === type) {
        return {
          addNodePermission: addNodePermission.controls,
          editNodePermission: editNodePermission.controls,
          removeNodePermission: removeNodePermission.controls,
        };
      } else if ('escalation-factor-node' === type) {
        return {
          addNodePermission: addNodePermission.escalationFactors,
          editNodePermission: editNodePermission.escalationFactors,
          removeNodePermission: removeNodePermission.escalationFactors,
        };
      } else if ('escalation-factor-barrier-node' === type) {
        return {
          addNodePermission: addNodePermission.escalationFactorBarriers,
          editNodePermission: editNodePermission.escalationFactorBarriers,
          removeNodePermission: removeNodePermission.escalationFactorBarriers,
        };
      } else {
        return { addNodePermission: false, editNodePermission: false, removeNodePermission: false };
      }
    },
    [addNodePermission, editNodePermission, removeNodePermission]
  );

  return (
    <AppPermissionsContext.Provider
      value={{
        canCreateBackendRecordsPermission,
        addNodePermission,
        editNodePermission,
        removeNodePermission,
        resolvePermissionsByNodeType,
      }}
      data-cy="provider"
    >
      {children}
    </AppPermissionsContext.Provider>
  );
};

// context consumer hook
export const useAppPermissionsContext = () => {
  const context = useContext(AppPermissionsContext);

  if (context === undefined) {
    throw new Error('useAppPermissionsContext must be used within a AppPermissionsProvider');
  }

  return context;
};
